import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { BrandMark } from '../components/layout/BrandMark';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authClient } from '../lib/auth-client';

const searchSchema = z.object({ token: z.string().optional() });

export const Route = createFileRoute('/reset-password')({
  validateSearch: searchSchema,
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const { token } = useSearch({ from: '/reset-password' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Lösenorden matchar inte.');
      return;
    }
    if (!token) {
      setError('Ogiltig återställningslänk.');
      return;
    }
    setLoading(true);
    try {
      const result = await authClient.resetPassword({
        newPassword: password,
        token,
      });
      if (result.error) {
        setError('Kunde inte återställa lösenordet. Länken kanske har gått ut.');
      } else {
        void navigate({ to: '/login' });
      }
    } catch {
      setError('Något gick fel. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-svh flex-col items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex justify-center">
          <BrandMark />
        </div>
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-7">
          <h1 className="text-2xl font-semibold tracking-tight">Nytt lösenord</h1>
          <p className="mt-1 text-sm text-muted-foreground">Välj ett lösenord på minst 8 tecken.</p>
          <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
            <div className="space-y-2">
              <Label htmlFor="reset-password">Nytt lösenord</Label>
              <Input
                id="reset-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={8}
                className="h-10"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reset-confirm">Bekräfta lösenord</Label>
              <Input
                id="reset-confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
                className="h-10"
              />
            </div>
            {error && (
              <p
                role="alert"
                className="rounded-lg bg-negative-soft px-3 py-2 text-sm text-negative"
              >
                {error}
              </p>
            )}
            <Button type="submit" disabled={loading} className="mt-1 w-full" size="lg">
              {loading ? 'Sparar…' : 'Spara nytt lösenord'}
            </Button>
          </form>
        </div>
        <p className="mt-5 text-center text-sm">
          <Link to="/login" className="font-medium text-primary hover:underline">
            Tillbaka till inloggning
          </Link>
        </p>
      </div>
    </div>
  );
}
